import { Timestamp } from '@bufbuild/protobuf';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MY_ASSIGNED_ACTIONS_KEY } from '../../../components/delivery/widgets/useMyAssignedActions';
import { deliveryClient } from '../../../lib/connect/clients';
import { parseLocalDate } from '../_shared/api/nullable';
import { makeEntityHooks } from '../_shared/hooks/makeEntityHooks';
// ============================================================================
// Audit — Create/Update/List (no Delete). Hand-written hooks because the
// Create and Update request shapes differ (Create takes a full set of
// initial fields; Update only assignedTo/notes — state transitions go
// through the dedicated Submit/Review/Close RPCs and the server rejects
// an explicit state here).
// ============================================================================
const AUDIT_KEY = 'audit';
export function useAudits(programId) {
    return useQuery({
        queryKey: [
            AUDIT_KEY,
            'list',
            programId
        ],
        queryFn: async ()=>{
            if (!programId) return {
                items: []
            };
            const resp = await deliveryClient.listAudits({
                programId
            });
            return {
                items: resp.audits
            };
        },
        enabled: !!programId,
        staleTime: 30000
    });
}
export function useAudit(id) {
    return useQuery({
        queryKey: [
            AUDIT_KEY,
            'get',
            id
        ],
        queryFn: async ()=>{
            if (!id) return null;
            const resp = await deliveryClient.getAudit({
                id
            });
            return resp.audit ?? null;
        },
        enabled: !!id,
        staleTime: 30000
    });
}
// ============================================================================
// Planned monthly-audit workflow (Audits guide): Start → In Progress →
// (Corrective Actions Open) → Complete, with a resolve/verify loop on each
// corrective action. These layer on the CRUD above; they invalidate the audit,
// question, and corrective-action caches because a single transition can move
// all three (verifying a CA flips its linked checklist item to Pass and shifts
// the score).
// ============================================================================
// Query-key roots the makeEntityHooks factories use (see below). Kept here so a
// workflow mutation can refresh the checklist + action lists it touches.
const AUDIT_QUESTION_KEY = 'audit question';
const CORRECTIVE_ACTION_KEY = 'correction';
function invalidateAuditWorkflow(qc) {
    qc.invalidateQueries({
        queryKey: [
            AUDIT_KEY
        ]
    });
    qc.invalidateQueries({
        queryKey: [
            AUDIT_QUESTION_KEY
        ]
    });
    qc.invalidateQueries({
        queryKey: [
            CORRECTIVE_ACTION_KEY
        ]
    });
}
// StartAudit creates (or returns the already-started) monthly full-program
// audit from the locked standard checklist. periodMonth defaults server-side to
// the current calendar month.
export function useStartAudit() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (req)=>{
            const resp = await deliveryClient.startAudit({
                programId: req.programId,
                periodMonth: req.periodMonth ?? ''
            });
            return resp.audit ?? null;
        },
        onSuccess: ()=>{
            invalidateAuditWorkflow(qc);
            toast.success('Audit started');
        },
        onError: (err)=>{
            toast.error(`Failed to start audit: ${err.message}`);
        }
    });
}
export function useCompleteAudit() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (req)=>{
            const resp = await deliveryClient.completeAudit(req);
            return resp.audit ?? null;
        },
        onSuccess: ()=>{
            invalidateAuditWorkflow(qc);
            toast.success('Audit completed');
        },
        onError: (err)=>{
            toast.error(`Failed to complete audit: ${err.message}`);
        }
    });
}
// ResolveCorrectiveAction: the section owner records their fix (→ Resolved).
export function useResolveCorrectiveAction() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (req)=>{
            const resp = await deliveryClient.resolveCorrectiveAction(req);
            return resp.action ?? null;
        },
        onSuccess: ()=>{
            invalidateAuditWorkflow(qc);
            // SAD-143: a Correction resolved from the assigned-actions feed (the
            // global dashboard's non-member surface, or Program Home/Workroom's
            // Mine group) must leave that feed too — it's a separate cache from
            // the audit-workflow keys above.
            qc.invalidateQueries({
                queryKey: [
                    MY_ASSIGNED_ACTIONS_KEY
                ]
            });
            toast.success('Correction resolved');
        },
        onError: (err)=>{
            toast.error(`Failed to resolve correction: ${err.message}`);
        }
    });
}
// VerifyCorrectiveAction: the auditor accepts (→ Verified, linked item flips to
// Pass) or rejects (→ back to Open for rework).
export function useVerifyCorrectiveAction() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (req)=>{
            const resp = await deliveryClient.verifyCorrectiveAction(req);
            return resp.action ?? null;
        },
        onSuccess: (_data, vars)=>{
            invalidateAuditWorkflow(qc);
            toast.success(vars.accepted ? 'Correction verified' : 'Sent back for rework');
        },
        onError: (err)=>{
            toast.error(`Failed to verify correction: ${err.message}`);
        }
    });
}
// Withdraw an action created in error (wrong item, duplicate, or an item since
// re-recorded Pass/N/A). It moves to the terminal Cancelled state — which lets
// the audit complete — without touching the linked checklist item. This is the
// supported "remove" path (delete is not); auditor-only, enforced server-side.
export function useWithdrawCorrectiveAction() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (req)=>{
            const resp = await deliveryClient.withdrawCorrectiveAction(req);
            return resp.action ?? null;
        },
        onSuccess: ()=>{
            invalidateAuditWorkflow(qc);
            toast.success('Correction withdrawn');
        },
        onError: (err)=>{
            toast.error(`Failed to withdraw correction: ${err.message}`);
        }
    });
}
// The per-program responsibility → owner mapping. A failed checklist item's
// corrective action prepopulates its owner from here.
export function useAuditSectionOwners(programId) {
    return useQuery({
        queryKey: [
            'audit section owner',
            'list',
            programId
        ],
        queryFn: async ()=>{
            if (!programId) return {
                items: []
            };
            const resp = await deliveryClient.listAuditSectionOwners({
                programId
            });
            return {
                items: resp.owners
            };
        },
        enabled: !!programId,
        staleTime: 30000
    });
}
// ============================================================================
// AuditQuestion — Upsert/List, no Delete
// ============================================================================
export const auditQuestionHooks = makeEntityHooks({
    key: 'audit question',
    list: async (req)=>{
        const resp = await deliveryClient.listAuditQuestions(req);
        return {
            items: resp.questions
        };
    },
    upsert: async (req)=>{
        const resp = await deliveryClient.upsertAuditQuestion(req);
        return resp.question;
    },
    remove: async ()=>{
        throw new Error('AuditQuestion delete is not supported');
    },
    entityId: (q)=>q.id,
    // Recording a result changes the audit's live score and can flip its state,
    // both read from the audit query — invalidate it so the detail header and the
    // history row don't go stale after a Pass/Fail/N/A.
    extraInvalidateKeys: [
        AUDIT_KEY
    ]
});
// ============================================================================
// CorrectiveAction — Upsert/List, no Delete
// ============================================================================
export const correctiveActionHooks = makeEntityHooks({
    key: 'correction',
    list: async (req)=>{
        const resp = await deliveryClient.listCorrectiveActions(req);
        return {
            items: resp.actions
        };
    },
    upsert: async (req)=>{
        const resp = await deliveryClient.upsertCorrectiveAction({
            ...req,
            dueDate: req.dueDate ? Timestamp.fromDate(parseLocalDate(req.dueDate)) : undefined
        });
        return resp.action;
    },
    remove: async ()=>{
        throw new Error('CorrectiveAction delete is not supported');
    },
    entityId: (a)=>a.id
});
// ============================================================================
// Deviation — Upsert/List/Delete (full surface)
// ============================================================================
export const deviationHooks = makeEntityHooks({
    key: 'deviation',
    list: async (req)=>{
        const resp = await deliveryClient.listDeviations(req);
        return {
            items: resp.deviations
        };
    },
    upsert: async (req)=>{
        const resp = await deliveryClient.upsertDeviation(req);
        return resp.deviation;
    },
    remove: async (id)=>{
        await deliveryClient.deleteDeviation({
            id
        });
    },
    entityId: (d)=>d.id
});
